// src/screens/mypage/WriteReviewScreen.js
// 리뷰 작성 화면
// params: { reservationId, productId, productName }
// 작성 완료 시 100P 자동 적립 (reviewService.addReview 내부 처리)
// 28차: 탈출 결과(성공/실패) 선택 UI 추가 → updateEscapeStats 연동
// 51차: 색 theme.js 단일화 — COLORS 참조 / 탈출성공 초록 통일
// 71차: reviewer 폴백을 profile?.displayName(존재한 적 없는 필드) → user?.displayName(Firebase Auth)로 수정
// 74차: 이메일 미인증 계정의 리뷰 작성 차단 — 이메일 인증을 예약까지 막으면 마찰이 너무
//       커서, 다른 사용자 신뢰에 영향을 주는 리뷰(공개 콘텐츠) 작성 시점에만 게이팅
// 74차: reviewerCrewName 스냅샷 추가 (크루 소속 표시)
// 75차: 자체 커스텀 헤더 → 공통 AppHeader로 교체(다른 화면과 동일 패턴 누락분). AppHeader가
// 자체적으로 top 세이프에어리어를 처리하므로 SafeAreaView는 top을 빼고 bottom/left/right만.
// 78차: 제출/확인 버튼(인라인 골드 fill)을 공용 GradientButton(Primary)으로 교체 — 로딩 중
// 스피너 대신 기존 RecruitDetailScreen 등에서 쓰던 "N 중..." 텍스트 스왑 패턴으로 통일.

// 80차 후속: route.params.bookingFormat==='show'(체험형 공연)면 탈출 성공/실패 선택 UI와
//       완료 화면의 성공/실패 아이콘·문구를 숨김(개념 자체가 없음). 호출부(ReservationHistoryScreen/
//       MyReviewsScreen)에서 예약의 bookingFormat을 그대로 넘겨줌.
// 78차 32차: 사진 삭제(✕) 버튼에 accessibilityLabel 추가(접근성 라벨 전수 점검)
// 78차 33차: updateEscapeStats 죽은 호출 제거 — recruitService.js에 이 함수가 애초에
// 존재하지 않아 매번 조용히 실패하고 있었음(catch로만 감싸져 있어 아무도 눈치 못 챔).
// 실제 탈출 성공/실패 통계(EscapeDashboardScreen.js)는 이 값이 아니라 매장관리자가
// 입력하는 reservations.success를 직접 집계해서 쓰고 있어 원래도 이 호출은 불필요했음.
import React, { useState } from 'react';
import { showAlert } from '../../components/common/AppAlert';
import {
  View, Text, TextInput, TouchableOpacity, StyleSheet,
  ScrollView, Image, KeyboardAvoidingView, Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import * as ImagePicker from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';   // ← 34차: 업로드 전 압축
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage, auth } from '../../services/firebase';
import { addReview } from '../../services/reviewService';
import { useAuth } from '../../contexts/AuthContext';
import AppHeader from '../../components/common/AppHeader';
import GradientButton from '../../components/common/GradientButton';
import {
  CameraIcon, EscapeSuccessIcon, EscapeFailIcon, IconLabel,
  StarIcon, DifficultyRatingIcon, ProductionRatingIcon, SpoilerWarningIcon,
  RewardIcon, CheckmarkIcon, CancelIcon,
} from '../../components/common/Icons';
import { LogoMark } from '../../components/common/AppHeader';
import { COLORS } from '../../constants/theme';

const GOLD = COLORS.gold, BG = COLORS.bg, SURFACE = COLORS.surface, SURFACE2 = COLORS.surface2;
const BORDER = COLORS.border, TEXT = COLORS.text;
const TEXT_SEC = COLORS.textSec, TEXT_MUTED = COLORS.textMuted;
const RED = COLORS.red, GREEN = COLORS.green;

export default function WriteReviewScreen({ route, navigation }) {
  const { reservationId, productId, productName, bookingFormat } = route.params || {};
  // 80차 후속: 체험형 공연은 탈출 성공/실패 개념이 없어 그 선택지 자체를 숨김
  const isShowFormat = bookingFormat === 'show';
  const { user, profile, refreshProfile } = useAuth();

  const [rating,       setRating]       = useState(0);
  const [comment,      setComment]      = useState('');
  const [loading,      setLoading]      = useState(false);
  const [submitted,    setSubmitted]    = useState(false);
  const [imageUris,    setImageUris]    = useState([]);
  const [uploading,    setUploading]    = useState(false);
  // 28차: 탈출 결과 — null: 미선택, true: 성공, false: 실패
  const [escapeResult, setEscapeResult] = useState(null);
  // 항목별 점수 (공포도/난이도/연출/스토리) — 0: 미선택
  const [subRatings, setSubRatings] = useState({ horror: 0, difficulty: 0, production: 0, story: 0 });
  // 스포일러 포함 여부
  const [spoiler, setSpoiler] = useState(false);

  // 이미지 선택 (갤러리)
  const handlePickImage = async () => {
    if (imageUris.length >= 3) {
      showAlert('알림', '사진은 최대 3장까지 첨부할 수 있어요.');
      return;
    }
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      showAlert('권한 필요', '사진 접근 권한이 필요해요. 설정에서 허용해주세요.');
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsMultipleSelection: false,
      quality: 0.7,
      aspect: [4, 3],
    });
    if (!result.canceled && result.assets?.[0]) {
      setImageUris(prev => [...prev, result.assets[0].uri]);
    }
  };

  // 34차: 업로드 전 클라이언트 압축 — 최대 1200px, quality 0.75
  // Cloud Function resizeImage가 추가로 썸네일/중간 사이즈 생성
  const compressImage = async (uri) => {
    try {
      const result = await ImageManipulator.manipulateAsync(
        uri,
        [{ resize: { width: 1200 } }],  // 높이는 비율 자동 유지
        { compress: 0.75, format: ImageManipulator.SaveFormat.JPEG }
      );
      return result.uri;
    } catch (e) {
      console.warn('[compressImage] 압축 실패, 원본 사용:', e.message);
      return uri;  // 실패 시 원본 그대로
    }
  };

  // Firebase Storage 업로드 → download URL 반환
  // 80차 후속: [버그 수정, fork로 발견] 사진 중 하나가 업로드 실패해도 무음으로 넘어가서,
  // 3장 선택했는데 실제론 2장만 반영된 채 제출되는데도 사용자는 3장 다 올라간 줄 알았음 —
  // 실패 개수를 반환해 호출부(handleSubmit)가 알려주도록 함.
  const uploadImages = async (uris) => {
    const urls = [];
    let failedCount = 0;
    for (const uri of uris) {
      try {
        const compressed = await compressImage(uri);
        const res        = await fetch(compressed);
        const blob       = await res.blob();
        const filename   = `reviews/${user.uid}_${Date.now()}_${Math.random().toString(36).slice(2)}.jpg`;
        const storageRef = ref(storage, filename);
        await uploadBytes(storageRef, blob);
        const url = await getDownloadURL(storageRef);
        urls.push(url);
      } catch (e) {
        console.warn('[uploadImages] 업로드 실패:', e.message);
        failedCount++;
      }
    }
    return { urls, failedCount };
  };

  const handleSubmit = async () => {
    // 74차: 이메일 가입 계정은 이메일 인증 완료해야 리뷰 작성 가능 — 리뷰는 다른 사용자의
    // 신뢰에 영향을 주는 공개 콘텐츠라 스팸/어뷰징 억제 목적. 예약(현장결제)까지는 막지
    // 않고 리뷰 작성 시점에만 게이팅. SNS 로그인 계정은 provider가 이미 검증한 이메일이라 대상 아님.
    if (profile?.provider === 'email' && auth.currentUser && !auth.currentUser.emailVerified) {
      showAlert(
        '이메일 인증 필요',
        '리뷰는 이메일 인증 후 작성할 수 있어요. 마이페이지 > 개인정보에서 인증 메일을 재전송할 수 있습니다.'
      );
      return;
    }
    if (rating === 0)        { showAlert('알림', '별점을 선택해주세요.'); return; }
    if (!comment.trim())     { showAlert('알림', '리뷰 내용을 입력해주세요.'); return; }
    if (!isShowFormat && escapeResult === null) { showAlert('알림', '탈출 결과를 선택해주세요.'); return; }

    try {
      setLoading(true);
      setUploading(true);
      let imageUrls = [];
      if (imageUris.length > 0) {
        const uploadResult = await uploadImages(imageUris);
        imageUrls = uploadResult.urls;
        if (uploadResult.failedCount > 0) {
          showAlert('알림', `사진 ${uploadResult.failedCount}장 업로드에 실패해서 제외하고 등록해요.`);
        }
      }
      setUploading(false);

      await addReview({
        uid:           user.uid,
        reviewer:      profile?.nickname || user?.displayName || '익명',
        reviewerCrewName: profile?.crewName || null,
        productId:     productId || '',
        productName:   productName || '',
        reservationId: reservationId || '',
        rating,
        comment:       comment.trim(),
        imageUris:     imageUrls,
        subRatings,
        spoiler,
        // 79차: [버그 수정] 화면에서 필수로 고르게 해놓고 실제로는 저장 호출에 빠져있었음
        escapeResult,
      });

      try { await refreshProfile(); } catch {}
      setSubmitted(true);
    } catch (e) {
      const errMsg = e?.message === '이미 리뷰를 작성한 테마입니다.' ? e.message : '리뷰 작성 중 오류가 발생했습니다.';
      showAlert('오류', errMsg);
    } finally {
      setLoading(false);
    }
  };

  // ── 완료 화면 ──
  if (submitted) return (
    <SafeAreaView style={s.container}>
      <View style={s.successWrap}>
        {!isShowFormat && (
          <View style={s.successIcon}>
            {escapeResult ? <EscapeSuccessIcon size={72} /> : <EscapeFailIcon size={72} />}
          </View>
        )}
        <Text style={s.successTitle}>리뷰가 등록되었습니다!</Text>
        {!isShowFormat && (
          <Text style={s.successSub}>
            {escapeResult ? '탈출 성공으로 기록되었어요!' : '탈출 실패로 기록되었어요. 다음엔 꼭!'}
          </Text>
        )}
        <View style={s.pointBadge}>
          <IconLabel Icon={RewardIcon} size={12} color={s.pointBadgeText.color} textStyle={s.pointBadgeText}>+100P 적립 완료</IconLabel>
        </View>
        <GradientButton label="확인" onPress={() => navigation.goBack()} style={{ width: '100%' }} textStyle={{ fontSize: 17 }} />
      </View>
    </SafeAreaView>
  );

  return (
    <SafeAreaView style={s.container} edges={['bottom', 'left', 'right']}>
      <AppHeader showBack centerElement={<Text style={s.headerTitle}>리뷰 작성</Text>} />

      {/* 80차 후속: [버그 수정, fork로 발견] 형제 화면(EditProfileScreen.js)엔 있는데
          여기만 빠져 있었음 — 500자 멀티라인 입력란이 화면 중간에 있어 iOS에서 타이핑
          중 키보드에 가려질 수 있었음. */}
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={{ flex: 1 }}>
      <ScrollView contentContainerStyle={s.content} keyboardShouldPersistTaps="handled">
        {/* 테마명 */}
        <View style={s.themeBox}>
          <View style={s.themeBoxHeader}>
            <View style={s.themeIconWrap}>
              <LogoMark size={20} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={s.themeLabel}>리뷰 작성</Text>
              <Text style={s.themeName} numberOfLines={2}>{productName || '테마'}</Text>
            </View>
            <View style={s.pointChip}>
              <Text style={s.pointChipTxt}>+100P</Text>
            </View>
          </View>
        </View>

        {/* 탈출 결과 선택 (28차 추가) — 80차 후속: 체험형 공연은 이 개념이 없어 숨김 */}
        {!isShowFormat && (
          <View style={s.section}>
            <Text style={s.sectionTitle}>탈출 결과 *</Text>
            <View style={s.escapeResultRow}>
              <TouchableOpacity
                style={[s.resultBtn, escapeResult === true && s.resultBtnSuccess]}
                onPress={() => setEscapeResult(true)}
                activeOpacity={0.8}>
                <EscapeSuccessIcon size={40} />
                <Text style={[s.resultLabel, escapeResult === true && { color: GREEN, fontWeight: '800' }]}>
                  탈출 성공
                </Text>
                {escapeResult === true && (
                  <View style={s.resultCheck}><CheckmarkIcon size={10} color={BG} /></View>
                )}
              </TouchableOpacity>
              <TouchableOpacity
                style={[s.resultBtn, escapeResult === false && s.resultBtnFail]}
                onPress={() => setEscapeResult(false)}
                activeOpacity={0.8}>
                <EscapeFailIcon size={40} />
                <Text style={[s.resultLabel, escapeResult === false && { color: RED, fontWeight: '800' }]}>
                  탈출 실패
                </Text>
                {escapeResult === false && (
                  <View style={[s.resultCheck, { backgroundColor: RED }]}><CheckmarkIcon size={10} color="#fff" /></View>
                )}
              </TouchableOpacity>
            </View>
            {escapeResult === null && (
              <Text style={s.resultHint}>탈출 결과는 탈출 통계에 반영됩니다</Text>
            )}
          </View>
        )}

        {/* 별점 */}
        <View style={s.section}>
          <Text style={s.sectionTitle}>별점 *</Text>
          <View style={s.starRow}>
            {[1, 2, 3, 4, 5].map(n => (
              <TouchableOpacity key={n} onPress={() => setRating(n)} style={s.starBtn}>
                <StarIcon size={32} filled={n <= rating} />
              </TouchableOpacity>
            ))}
          </View>
          <Text style={s.ratingLabel}>
            {rating === 0 ? '별점을 선택해주세요' :
             rating === 1 ? '별로예요' :
             rating === 2 ? '그저 그래요' :
             rating === 3 ? '보통이에요' :
             rating === 4 ? '좋아요' : '최고예요!'}
          </Text>
        </View>

        {/* 항목별 점수 */}
        <View style={s.section}>
          <Text style={s.sectionTitle}>항목별 점수 <Text style={{ color: TEXT_MUTED, fontSize: 12, fontWeight: '400' }}>선택</Text></Text>
          {[
            { key: 'horror',     label: '👻 공포도' },
            // 80차 후속: [버그 수정] escapeResult 섹션(성공/실패)은 이미 !isShowFormat으로
            // 가려져 있는데, 바로 아래 이 항목별 점수는 형식 분기가 없어서 체험형 리뷰를
            // 쓸 때도 "난이도" 별점을 매기라고 나오고 있었음(퍼즐 개념 자체가 없는데) —
            // 난이도 개념이 없는 형식이므로 이 항목만 제외(공포도/연출/스토리는 체험형에도 적용됨).
            ...(isShowFormat ? [] : [{ key: 'difficulty', label: '난이도', Icon: DifficultyRatingIcon }]),
            { key: 'production', label: '연출·인테리어', Icon: ProductionRatingIcon },
            { key: 'story',      label: '📖 스토리' },
          ].map(({ key, label, Icon }) => (
            <View key={key} style={s.subRatingRow}>
              {Icon
                ? <IconLabel Icon={Icon} size={13} color={TEXT_SEC} textStyle={s.subRatingLabel}>{label}</IconLabel>
                : <Text style={s.subRatingLabel}>{label}</Text>}
              <View style={{ flexDirection: 'row', gap: 4 }}>
                {[1, 2, 3, 4, 5].map(n => (
                  <TouchableOpacity
                    key={n}
                    onPress={() => setSubRatings(prev => ({ ...prev, [key]: prev[key] === n ? 0 : n }))}
                    hitSlop={{ top: 6, bottom: 6, left: 4, right: 4 }}>
                    <StarIcon size={22} filled={n <= subRatings[key]} emptyColor="#2a2a3a" />
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          ))}
        </View>

        {/* 리뷰 내용 */}
        <View style={s.section}>
          <Text style={s.sectionTitle}>리뷰 내용 * <Text style={{ color: TEXT_MUTED, fontSize: 12, fontWeight: '400' }}>({comment.length}/500)</Text></Text>
          <TextInput
            style={s.textArea}
            value={comment}
            onChangeText={setComment}
            placeholder="테마에 대한 솔직한 후기를 남겨주세요&#10;(힌트/스포일러 없이 작성해 주세요)"
            placeholderTextColor={TEXT_MUTED}
            multiline
            maxLength={500}
            textAlignVertical="top"
          />
          {/* 스포일러 토글 */}
          <TouchableOpacity
            style={[s.spoilerRow, spoiler && s.spoilerRowOn]}
            onPress={() => setSpoiler(v => !v)}
            activeOpacity={0.8}>
            <View style={[s.spoilerCheck, spoiler && s.spoilerCheckOn]}>
              {spoiler && <CheckmarkIcon size={11} color={BG} />}
            </View>
            <View style={{ flex: 1 }}>
              <IconLabel Icon={SpoilerWarningIcon} size={13} color={spoiler ? '#FBBF24' : TEXT_SEC} textStyle={[s.spoilerLabel, spoiler && { color: '#FBBF24' }]}>
                스포일러 포함
              </IconLabel>
              <Text style={s.spoilerDesc}>
                힌트 수, 장치 구조 등 스포일러가 있으면 체크해주세요. 다른 유저에게 가려져 표시됩니다.
              </Text>
            </View>
          </TouchableOpacity>
        </View>

        {/* 사진 첨부 */}
        <View style={s.section}>
          <Text style={s.sectionTitle}>
            사진 첨부 <Text style={{ color: TEXT_MUTED, fontSize: 12, fontWeight: '400' }}>선택 · 최대 3장</Text>
          </Text>
          <View style={{ flexDirection: 'row', gap: 10, flexWrap: 'wrap' }}>
            {imageUris.map((uri, idx) => (
              <View key={idx} style={s.imgThumbWrap}>
                <Image source={{ uri }} style={s.imgThumb} />
                <TouchableOpacity
                  style={s.imgRemoveBtn}
                  onPress={() => setImageUris(prev => prev.filter((_, i) => i !== idx))}
                  accessibilityLabel="사진 삭제">
                  <CancelIcon size={s.imgRemoveTxt.fontSize || 14} color={s.imgRemoveTxt.color} />
                </TouchableOpacity>
              </View>
            ))}
            {imageUris.length < 3 && (
              <TouchableOpacity style={s.imgAddBtn} onPress={handlePickImage}>
                <CameraIcon size={22} color={TEXT_MUTED} />
                <Text style={s.imgAddTxt}>{imageUris.length === 0 ? '사진 추가' : '추가'}</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* 포인트 안내 */}
        <View style={s.pointNotice}>
          <IconLabel Icon={RewardIcon} size={13} color={GOLD} textStyle={s.pointNoticeText}>리뷰 작성 완료 시 100P가 자동으로 적립됩니다</IconLabel>
        </View>

        {/* 제출 */}
        <GradientButton
          label={uploading ? '사진 업로드 중...' : loading ? '등록 중...' : '리뷰 등록하기'}
          onPress={handleSubmit}
          disabled={loading || uploading || rating === 0 || (!isShowFormat && escapeResult === null)}
        />

        <View style={{ height: 40 }} />
      </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const s = StyleSheet.create({
  container:     { flex: 1, backgroundColor: BG },
  headerTitle:   { color: TEXT, fontSize: 15, fontWeight: '800' },
  content:       { padding: 20 },

  themeBox:      { backgroundColor: SURFACE, borderRadius: 14, padding: 16,
                   marginBottom: 24, borderWidth: 1, borderColor: 'rgba(212,168,67,0.25)' },
  themeBoxHeader:{ flexDirection: 'row', alignItems: 'center', gap: 12 },
  themeIconWrap: { width: 44, height: 44, borderRadius: 12, backgroundColor: GOLD + '18',
                   borderWidth: 1, borderColor: GOLD + '30',
                   alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  themeLabel:    { color: TEXT_MUTED, fontSize: 11, fontWeight: '600', letterSpacing: 0.5, marginBottom: 3 },
  themeName:     { color: TEXT, fontSize: 15, fontWeight: '800', lineHeight: 20 },
  pointChip:     { backgroundColor: GOLD + '18', borderRadius: 10, borderWidth: 1,
                   borderColor: GOLD + '40', paddingHorizontal: 10, paddingVertical: 5,
                   alignSelf: 'flex-start', flexShrink: 0 },
  pointChipTxt:  { color: GOLD, fontSize: 12, fontWeight: '800' },

  section:       { marginBottom: 24 },
  sectionTitle:  { color: TEXT, fontSize: 14, fontWeight: '700', marginBottom: 12 },

  // 탈출 결과 선택
  escapeResultRow: { flexDirection: 'row', gap: 12 },
  resultBtn:     { flex: 1, backgroundColor: SURFACE, borderRadius: 12, borderWidth: 1.5,
                   borderColor: BORDER, paddingVertical: 16, alignItems: 'center', gap: 6,
                   position: 'relative' },
  resultBtnSuccess: { borderColor: GREEN, backgroundColor: GREEN + '12' },
  resultBtnFail:    { borderColor: RED,   backgroundColor: RED   + '10' },
  resultLabel:   { color: TEXT_SEC, fontSize: 14, fontWeight: '600' },
  resultCheck:   { position: 'absolute', top: 8, right: 8,
                   width: 18, height: 18, borderRadius: 9,
                   backgroundColor: GREEN, alignItems: 'center', justifyContent: 'center' },
  resultHint:    { color: TEXT_MUTED, fontSize: 11, marginTop: 8, textAlign: 'center' },

  starRow:       { flexDirection: 'row', gap: 8, marginBottom: 10 },
  starBtn:       { padding: 4 },
  ratingLabel:   { color: TEXT_SEC, fontSize: 14, textAlign: 'center' },

  subRatingRow:  { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
                   paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: BORDER },
  subRatingLabel:{ color: TEXT_SEC, fontSize: 14, fontWeight: '600' },

  textArea:      { backgroundColor: SURFACE, borderRadius: 12, borderWidth: 1,
                   borderColor: BORDER, padding: 16, color: TEXT, fontSize: 14,
                   minHeight: 160, lineHeight: 22 },

  spoilerRow:    { flexDirection: 'row', alignItems: 'flex-start', gap: 10, marginTop: 10,
                   backgroundColor: SURFACE, borderRadius: 10, padding: 12,
                   borderWidth: 1, borderColor: BORDER },
  spoilerRowOn:  { borderColor: 'rgba(251,191,36,0.5)', backgroundColor: 'rgba(251,191,36,0.06)' },
  spoilerCheck:  { width: 20, height: 20, borderRadius: 5, borderWidth: 1.5,
                   borderColor: BORDER, backgroundColor: SURFACE2,
                   alignItems: 'center', justifyContent: 'center', marginTop: 1, flexShrink: 0 },
  spoilerCheckOn:{ backgroundColor: '#FBBF24', borderColor: '#FBBF24' },
  spoilerLabel:  { color: TEXT_SEC, fontSize: 13, fontWeight: '700', marginBottom: 3 },
  spoilerDesc:   { color: TEXT_MUTED, fontSize: 11, lineHeight: 16 },

  pointNotice:   { backgroundColor: GOLD + '10', borderRadius: 10, padding: 14,
                   borderWidth: 1, borderColor: GOLD + '30', marginBottom: 24 },
  pointNoticeText:{ color: GOLD, fontSize: 13, fontWeight: '600', textAlign: 'center' },

  // 완료 화면
  successWrap:   { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32 },
  successIcon:   { width: 100, height: 100, borderRadius: 50,
                   backgroundColor: GOLD + '20', justifyContent: 'center',
                   alignItems: 'center', marginBottom: 20,
                   borderWidth: 1, borderColor: GOLD + '40' },
  successTitle:  { color: TEXT, fontSize: 22, fontWeight: '900', marginBottom: 8 },
  successSub:    { color: TEXT_MUTED, fontSize: 14, marginBottom: 24, textAlign: 'center' },
  pointBadge:    { backgroundColor: GOLD + '18', borderRadius: 20, paddingHorizontal: 20,
                   paddingVertical: 10, marginBottom: 32,
                   borderWidth: 1, borderColor: GOLD + '40' },
  pointBadgeText:{ color: GOLD, fontSize: 14, fontWeight: '800' },

  // 이미지 첨부
  imgThumbWrap: { position: 'relative', width: 90, height: 90 },
  imgThumb:     { width: 90, height: 90, borderRadius: 10, backgroundColor: SURFACE2 },
  imgRemoveBtn: { position: 'absolute', top: -6, right: -6,
                  backgroundColor: RED, borderRadius: 10,
                  width: 20, height: 20, justifyContent: 'center', alignItems: 'center' },
  imgRemoveTxt: { color: '#fff', fontSize: 10, fontWeight: '900' },
  imgAddBtn:    { width: 90, height: 90, borderRadius: 10, borderWidth: 1.5,
                  borderColor: BORDER, borderStyle: 'dashed',
                  justifyContent: 'center', alignItems: 'center',
                  backgroundColor: SURFACE, gap: 4 },
  imgAddIcon:   { fontSize: 22 },
  imgAddTxt:    { color: TEXT_MUTED, fontSize: 11, fontWeight: '600' },
});